import { Shield, Award, Globe, CheckCircle } from "lucide-react";

const BADGE_URLS = {
  gold: "https://customer-assets.emergentagent.com/job_travel-quality-label/artifacts/3qf4e4f0_Black%20And%20White%20Simple%20Icon%20Logo.png",
  silver: "https://customer-assets.emergentagent.com/job_travel-quality-label/artifacts/80ndwcjh_Black%20And%20White%20Simple%20Icon%20Logo%20%281%29.png",
  bronze: "https://customer-assets.emergentagent.com/job_travel-quality-label/artifacts/o5vrg8qt_Black%20And%20White%20Simple%20Icon%20Logo%20%282%29.png"
};

const AboutSection = () => {
  const benefits = [
    {
      icon: Shield,
      title: "Trusted Quality Certification",
      description: "Earn credibility through independent certification that demonstrates your commitment to quality, hospitality excellence, safety, and guest satisfaction."
    },
    {
      icon: Globe,
      title: "Global Visibility",
      description: "Gain exposure in our worldwide directory of certified hotels and destinations."
    },
    {
      icon: Award,
      title: "Recognized Excellence",
      description: "Stand apart with the prestigious WTTQ Gold, Silver, or Bronze Certification Mark and join a select group of organizations recognized for excellence in tourism and hospitality."
    }
  ];

  const tiers = [
    {
      name: "Gold",
      badge: BADGE_URLS.gold,
      lead: "The Global Benchmark for Tourism & Hospitality Excellence.",
      description: "Gold Certification is awarded only to exceptional properties that demonstrate world-class performance across all WTTQ assessment categories.\n\nThese properties set the benchmark for excellence within their destination, region, or country.",
      criteria: ["Meets WTTQ Gold quality standards", "Exceptional hospitality", "Outstanding guest satisfaction", "Exceptional food & beverage experiences", "Strong sustainability leadership"]
    },
    {
      name: "Silver",
      badge: BADGE_URLS.silver,
      lead: "Elevated Experiences. Consistent Excellence.",
      description: "Silver Certification recognizes properties that exceed standard expectations and demonstrate excellence across hospitality, service culture, gastronomy, guest satisfaction, and community engagement.",
      criteria: ["Meets WTTQ Silver quality standards", "Higher service consistency", "Enhanced food & beverage offering", "Stronger community and sustainability engagement"]
    },
    {
      name: "Bronze",
      badge: BADGE_URLS.bronze,
      lead: "Reliable. Professional. Trusted.",
      description: "Bronze Certification recognizes tourism and hospitality businesses that consistently meet recognized quality standards and provide a safe, comfortable, and dependable guest experience.",
      criteria: ["Meets WTTQ Bronze quality standards", "Reliable operations and service delivery", "Clean, safe, and well-maintained facilities", "Positive guest satisfaction"]
    }
  ];

  return (
    <section 
      id="about" 
      className="py-20 lg:py-32 bg-[#FAFAFA]"
      data-testid="about-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-[#D4AF37] text-xs uppercase tracking-[0.2em] font-semibold mb-4">
            About WTTQ Certification
          </p>
          <h2 
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0B132B] mb-6 tracking-tight"
            data-testid="about-title"
          >
            The Mark of Quality in Tourism
          </h2>
          <div className="text-[#4A5568] text-base sm:text-lg max-w-3xl mx-auto leading-relaxed space-y-4 text-left sm:text-center">
            <p>
              The World Tourism &amp; Travel Quality Organization (WTTQ.Org) recognizes and certifies destinations, hotels, resorts, and tourism businesses that meet internationally benchmarked standards of excellence.
            </p>
            <p>
              Through independent evaluation and rigorous quality assessments, WTTQ Certification identifies organizations that deliver exceptional guest experiences, demonstrate operational excellence, embrace sustainability, and contribute positively to their communities and destinations.
            </p>
            <p className="font-semibold text-[#0B132B]">
              Trusted by travelers. Respected by the industry. Recognized worldwide.
            </p>
          </div>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          {benefits.map((benefit, index) => (
            <div 
              key={benefit.title}
              className="bg-white p-8 border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all duration-300"
              data-testid={`benefit-card-${index}`}
            >
              <div className="w-14 h-14 bg-[#0B132B] rounded-full flex items-center justify-center mb-6">
                <benefit.icon className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <h3 className="font-heading text-xl font-bold text-[#0B132B] mb-3">
                {benefit.title}
              </h3>
              <p className="text-[#4A5568] leading-relaxed">
                {benefit.description}
              </p>
            </div>
          ))}
        </div>

        {/* Quality Tiers */}
        <div className="text-center mb-12">
          <h3 className="font-heading text-2xl sm:text-3xl font-bold text-[#0B132B] mb-4">
            Quality Rating Tiers
          </h3>
          <p className="text-[#4A5568] max-w-2xl mx-auto">
            Our three-tier certification system recognizes excellence at every level
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {tiers.map((tier, index) => (
            <div 
              key={tier.name}
              className={`relative bg-white p-8 border-2 transition-all duration-300 hover:shadow-xl ${
                tier.name === "Gold" 
                  ? "border-[#D4AF37]" 
                  : tier.name === "Silver" 
                  ? "border-[#94A3B8]" 
                  : "border-[#CD7F32]"
              }`}
              data-testid={`tier-card-${tier.name.toLowerCase()}`}
            >
              {/* Badge */}
              <div className="flex justify-center mb-6">
                <img 
                  src={tier.badge} 
                  alt={`${tier.name} Quality Badge`}
                  className="w-24 h-24 object-contain"
                />
              </div>

              {/* Tier Name */}
              <h4 className={`font-heading text-2xl font-bold text-center mb-2 ${
                tier.name === "Gold" 
                  ? "text-[#92710d]" 
                  : tier.name === "Silver" 
                  ? "text-[#64748b]" 
                  : "text-[#a15f1c]"
              }`}>
                {tier.name} Quality
              </h4>

              <div className="text-[#4A5568] text-center mb-6">
                {tier.lead && (
                  <strong className="block text-[#0B132B] mb-2">{tier.lead}</strong>
                )}
                {tier.description.split("\n\n").map((para, i) => (
                  <p key={i} className={i > 0 ? "mt-3" : ""}>{para}</p>
                ))}
              </div>

              {/* Criteria List */}
              <ul className="space-y-3">
                {tier.criteria.map((criterion) => (
                  <li key={criterion} className="flex items-start gap-3">
                    <CheckCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                      tier.name === "Gold" 
                        ? "text-[#D4AF37]" 
                        : tier.name === "Silver" 
                        ? "text-[#94A3B8]" 
                        : "text-[#CD7F32]"
                    }`} />
                    <span className="text-[#4A5568] text-sm">{criterion}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
